import axios, { AxiosInstance, AxiosRequestConfig } from "axios";

import { saveBlob } from "./saveBlob";
import { getApiErrorMessage } from "../lib/api-errors";

function readBlobAsText(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(reader.error);
        reader.readAsText(blob);
    });
}

async function getBlobResponseErrorMessage(error: unknown): Promise<string | undefined> {
    const data = (error as { response?: { data?: unknown } })?.response?.data;

    if (!(data instanceof Blob)) {
        return getApiErrorMessage(error);
    }

    try {
        return getApiErrorMessage({ response: { data: JSON.parse(await readBlobAsText(data)) } });
    } catch {
        return undefined;
    }
}

export class DownloadError extends Error {
    /**
     * Message returned by the API. Absent when the request never got a response
     * body to read — a network failure, or a stream aborted after the headers.
     */
    readonly apiMessage?: string;

    constructor(message: string, apiMessage?: string) {
        super(message);
        this.name = "DownloadError";
        this.apiMessage = apiMessage;
    }
}

export type DownloadBlobFromUrlOptions = {
    client?: AxiosInstance;
    config?: AxiosRequestConfig;
    onProgress?: (progress: number | null) => void;
};

/**
 * Default `downloadFn` for `useDownload`: fetches `url` as a blob, saves it
 * as `filename`, and normalizes a failure to the API's error message.
 */
export async function downloadBlobFromUrl(
    url: string,
    filename: string,
    { client = axios, config, onProgress }: DownloadBlobFromUrlOptions = {},
): Promise<void> {
    try {
        const { data } = await client.get<Blob>(url, {
            ...config,
            responseType: "blob",
            onDownloadProgress: event => {
                onProgress?.(event.total ? Math.round((event.loaded / event.total) * 100) : null);
            },
        });

        saveBlob(data, filename);
    } catch (error) {
        const apiMessage = await getBlobResponseErrorMessage(error);

        throw new DownloadError(apiMessage ?? (error as Error).message, apiMessage);
    }
}
